/**
 * CAP Reports — the program-level reporting view.
 *
 * Everything reportable across the CAP portfolio: status, department, source,
 * urgency, aging, closure performance, CMS deadline risk, root-cause trends, and
 * created-vs-closed over time. Scoped on the server to what the user may see. Adds
 * an agentic committee-ready narrative summary and PDF/CSV export.
 */
import React, { useCallback, useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', validating: 'Validating', approved: 'Approved', closed: 'Closed', cancelled: 'Cancelled' };
const SOURCE_LABEL = { audit_report: 'Audit report', coa_deficiency: 'COA deficiency', priq_gap: 'RuleIQ gap', fdr_finding: 'FDR finding', manual: 'Manual' };

function Tile({ value, label, color }) {
  return (
    <div className="rounded-2xl ring-1 ring-slate-200/80 bg-white p-4 relative overflow-hidden">
      <span aria-hidden className="absolute -top-5 -right-5 w-14 h-14 rounded-full opacity-[0.12]" style={{ background: color }} />
      <div className="text-[26px] font-bold tabular-nums leading-none tracking-tight" style={{ color }}>{value}</div>
      <div className="text-[11px] font-semibold text-slate-500 mt-1.5">{label}</div>
    </div>
  );
}

function BarRow({ label, n, max, color }) {
  const pct = max > 0 ? Math.round((n / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-40 text-[12px] text-slate-600 truncate">{label}</div>
      <div className="flex-1 h-3.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="w-8 text-right text-[12px] font-semibold text-slate-700 tabular-nums">{n}</div>
    </div>
  );
}

export default function CapReports({ user, onBack }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [narrating, setNarrating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/api/pcap/reports/summary`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Failed to load report');
      setReport(d.report);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNarrative = async () => {
    setNarrating(true);
    try {
      const res = await fetch(`${API}/api/pcap/reports/narrative`, { credentials: 'include' });
      const d = await res.json();
      if (res.ok && d.success) setNarrative(d.narrative);
    } catch { /* non-fatal */ }
    finally { setNarrating(false); }
  };
  const download = async (path, filename) => {
    const res = await fetch(`${API}${path}`, { credentials: 'include' });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
  };

  const h = report?.headline || {};
  const maxStatus = Math.max(1, ...(report?.by_status || []).map((r) => r.n));
  const maxSource = Math.max(1, ...(report?.by_source || []).map((r) => r.n));
  const ag = report?.aging || {};
  const cms = report?.cms_deadline_risk || {};
  const trendMax = Math.max(1, ...(report?.trend || []).flatMap((t) => [t.created, t.closed]));

  return (
    <div className="max-w-6xl mx-auto px-6 pt-10 pb-20">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-900 mb-6 inline-flex items-center gap-1.5">← Back to CAPs</button>

      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-orange-500 mb-1">Corrective Action Program</p>
          <h1 className="text-[28px] font-semibold text-slate-900 leading-tight tracking-tight">Reports</h1>
          {report?.scope_limited && <p className="text-[12px] text-slate-500 mt-1">Showing the CAPs you’re authorized to see (your department(s) and CAPs assigned to you).</p>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => download('/api/pcap/reports/summary?format=csv', 'cap-program-report.csv')} className="text-[13px] font-medium text-slate-700 bg-white ring-1 ring-slate-200 rounded-full px-4 py-2 hover:bg-slate-50">Export CSV</button>
          <button onClick={() => download('/api/pcap/reports/pdf', 'CAP-Program-Report.pdf')} className="text-[13px] font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-full px-4 py-2">Download PDF</button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Building your report…</div>
      ) : error ? (
        <div className="rounded-lg bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800">{error}</div>
      ) : (
        <div className="space-y-6">
          {/* Agentic summary */}
          <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-5 text-white">
            <div className="flex items-center justify-between gap-3">
              <div className="inline-flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-white/10 text-[10px] font-semibold px-2 py-0.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> CAP Agent</span>
                <span className="text-[14px] font-semibold">Committee summary</span>
              </div>
              <button onClick={runNarrative} disabled={narrating} className="text-[12px] font-medium text-slate-900 bg-white hover:bg-slate-100 disabled:opacity-50 rounded-full px-3.5 py-1.5">
                {narrating ? 'Writing…' : narrative ? 'Regenerate' : 'Generate summary'}
              </button>
            </div>
            {narrative ? (
              <div className="mt-3 space-y-2">
                {narrative.headline && <p className="text-[15px] font-semibold">{narrative.headline}</p>}
                {narrative.narrative && <p className="text-[13px] text-slate-200 leading-relaxed">{narrative.narrative}</p>}
                {narrative.watch_items?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {narrative.watch_items.map((w, i) => <li key={i} className="text-[12px] text-amber-200">• {w}</li>)}
                  </ul>
                )}
              </div>
            ) : (
              <p className="mt-2 text-[13px] text-slate-300">Generate a board-ready narrative of the portfolio, grounded in the figures below.</p>
            )}
          </div>

          {/* Headline tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Tile value={h.total} label="Total CAPs" color="#0E2841" />
            <Tile value={h.open} label="Open" color="#156082" />
            <Tile value={h.overdue} label="Overdue" color="#DC2626" />
            <Tile value={`${h.closure_rate_pct ?? 0}%`} label="Closure rate" color="#196B24" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Tile value={h.closed} label="Closed" color="#196B24" />
            <Tile value={h.avg_days_to_close ?? '—'} label="Avg days to close" color="#F58220" />
            <Tile value={h.on_time_closure_pct != null ? `${h.on_time_closure_pct}%` : '—'} label="On-time closure" color="#156082" />
            <Tile value={h.fdr} label="FDR-scope CAPs" color="#B45309" />
          </div>

          {/* CMS deadline risk */}
          {((cms.past_30d_submit || 0) + (cms.past_180d_validation || 0) > 0) && (
            <div className="rounded-2xl ring-1 ring-red-200 bg-red-50 p-4">
              <p className="text-[13px] font-semibold text-red-800">CMS deadline risk</p>
              <p className="text-[13px] text-red-700 mt-0.5">{cms.past_30d_submit} audit CAP(s) past the 30-day submission window · {cms.past_180d_validation} past the 180-day validation window.</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            {/* By status */}
            <div className="rounded-2xl ring-1 ring-slate-200/80 bg-white p-5">
              <h2 className="text-[14px] font-semibold text-slate-900 mb-3">By status</h2>
              <div className="space-y-2">
                {(report.by_status || []).map((r) => <BarRow key={r.status} label={STATUS_LABEL[r.status] || r.status} n={r.n} max={maxStatus} color="#156082" />)}
              </div>
            </div>
            {/* By source */}
            <div className="rounded-2xl ring-1 ring-slate-200/80 bg-white p-5">
              <h2 className="text-[14px] font-semibold text-slate-900 mb-3">By source</h2>
              <div className="space-y-2">
                {(report.by_source || []).map((r) => <BarRow key={r.source_type} label={SOURCE_LABEL[r.source_type] || r.source_type} n={r.n} max={maxSource} color="#F58220" />)}
              </div>
            </div>
          </div>

          {/* By department */}
          <div className="rounded-2xl ring-1 ring-slate-200/80 bg-white p-5">
            <h2 className="text-[14px] font-semibold text-slate-900 mb-3">By department</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="py-2 font-semibold">Department</th><th className="py-2 font-semibold text-right">Total</th><th className="py-2 font-semibold text-right">Open</th><th className="py-2 font-semibold text-right">Closed</th><th className="py-2 font-semibold text-right">Overdue</th>
                </tr></thead>
                <tbody>
                  {(report.by_department || []).map((r) => (
                    <tr key={r.department_id || r.department} className="border-b border-slate-50">
                      <td className="py-2 text-slate-800">{r.department}</td>
                      <td className="py-2 text-right tabular-nums">{r.total}</td>
                      <td className="py-2 text-right tabular-nums text-blue-700">{r.open}</td>
                      <td className="py-2 text-right tabular-nums text-emerald-700">{r.closed}</td>
                      <td className={`py-2 text-right tabular-nums font-semibold ${r.overdue > 0 ? 'text-red-600' : 'text-slate-400'}`}>{r.overdue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Aging + urgency + root cause */}
          <div className="grid md:grid-cols-3 gap-5">
            <div className="rounded-2xl ring-1 ring-slate-200/80 bg-white p-5">
              <h2 className="text-[14px] font-semibold text-slate-900 mb-3">Open CAP aging</h2>
              {[['0–30 days', ag.d0_30, '#196B24'], ['31–60 days', ag.d31_60, '#156082'], ['61–90 days', ag.d61_90, '#B45309'], ['over 90 days', ag.d90_plus, '#DC2626']].map(([l, n, c]) => (
                <BarRow key={l} label={l} n={n || 0} max={Math.max(1, ag.d0_30 || 0, ag.d31_60 || 0, ag.d61_90 || 0, ag.d90_plus || 0)} color={c} />
              ))}
            </div>
            <div className="rounded-2xl ring-1 ring-slate-200/80 bg-white p-5">
              <h2 className="text-[14px] font-semibold text-slate-900 mb-3">By urgency</h2>
              <div className="space-y-1.5">
                {(report.by_urgency || []).map((r) => <div key={r.urgency} className="flex justify-between text-[13px]"><span className="text-slate-600 capitalize">{r.urgency}</span><span className="font-semibold tabular-nums">{r.n}</span></div>)}
              </div>
            </div>
            <div className="rounded-2xl ring-1 ring-slate-200/80 bg-white p-5">
              <h2 className="text-[14px] font-semibold text-slate-900 mb-3">Root-cause categories</h2>
              {(report.by_root_cause || []).length === 0 ? <p className="text-[12px] text-slate-400">None confirmed yet.</p> : (
                <div className="space-y-1.5">
                  {report.by_root_cause.map((r) => <div key={r.category} className="flex justify-between text-[13px]"><span className="text-slate-600 capitalize">{r.category}</span><span className="font-semibold tabular-nums">{r.n}</span></div>)}
                </div>
              )}
            </div>
          </div>

          {/* Trend */}
          <div className="rounded-2xl ring-1 ring-slate-200/80 bg-white p-5">
            <h2 className="text-[14px] font-semibold text-slate-900 mb-3">Created vs closed (last 12 months)</h2>
            {(report.trend || []).length === 0 ? <p className="text-[12px] text-slate-400">Not enough history yet.</p> : (
              <div className="flex items-end gap-2 h-40">
                {report.trend.map((t) => (
                  <div key={t.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                    <div className="w-full flex items-end justify-center gap-0.5 h-32">
                      <div className="w-2.5 rounded-t bg-orange-500" style={{ height: `${(t.created / trendMax) * 100}%` }} title={`${t.created} created`} />
                      <div className="w-2.5 rounded-t bg-emerald-600" style={{ height: `${(t.closed / trendMax) * 100}%` }} title={`${t.closed} closed`} />
                    </div>
                    <span className="text-[9px] text-slate-400 truncate w-full text-center">{t.month.slice(2)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-orange-500" /> Created</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-600" /> Closed</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
